'use strict';
import find from 'lodash/find';
import isArray from 'lodash/isArray';
import isDate from 'lodash/isDate';
import isNaN from 'lodash/isNaN';
import isNumber from 'lodash/isNumber';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import {FORM_RESPONSE_COLLECTION} from './config';


const angular = require('angular');
const {copy, merge} = angular;

module.exports = function () {
  return {
    $get: [
      '$injector',
      'FirebaseUtil',
      'AuthService',
      'FirebaseConnectionService',
      'AbstractDomainClass',
      'OrgIdMixin',
      '$filter',
      '$domainValidation',
      'FormUtils',
      'FirebaseQueueService',
      'ROOT_API_COLLECTION',
      'ROOT_API_SPEC',
      function (
        $injector,
        FirebaseUtil,
        AuthService,
        FirebaseConnectionService,
        AbstractDomainClass,
        OrgIdMixin,
        $filter,
        $domainValidation,
        FormUtils,
        FirebaseQueueService,
        ROOT_API_COLLECTION,
        ROOT_API_SPEC,
      ) {

        // Service Locator Pattern
        var FormDomain;

        function FormResponseDomain(data) {
          AbstractDomainClass.call(this, FormResponseDomain, data);
        }

        AbstractDomainClass.__inheritFrom(FormResponseDomain, 'FormResponseDomain', FORM_RESPONSE_COLLECTION);
        OrgIdMixin(FormResponseDomain);

        FormResponseDomain.$constraints.addConstraints({
          form: {type: 'string'}, // domainClass: FormDomain id
          formVersion: {type: 'string'},
          target: {type: 'DomainLink', belongsTo: true, nullable: true},
          fields: {type: 'JSON', serializer: FormUtils.serialize, hydrator: FormUtils.hydrate},
          org: {type: 'string'}, // Organization ID

          createdBy: {type: 'DomainLink', domainClass: 'UserDomain'},
          dateCreated: {type: 'Date'},
          lastUpdated: {type: 'Date'}
        });

        // more methods go here;

        FormResponseDomain.createResponse = createResponse;
        FormResponseDomain.updateResponse = updateResponse;
        FormResponseDomain.createFromFormField = createFromFormField;
        FormResponseDomain.validateFieldResult = validateFieldResult;

        FormResponseDomain.prototype.getForm = getForm;
        FormResponseDomain.prototype.getFields = getFields;
        FormResponseDomain.prototype.toString = toString;

        function createResponse(response) {
          if (!FirebaseConnectionService.online) return Promise.reject({code: 'default.error.offline.message'});

          var request = {
            method: 'form-responses/create',
            orgId: response.org,
            form: response.form,
            formVersion: response.formVersion,
            fields: FormUtils.serializeDates(response.fields),
            target: {id: 'TESTING', collection: 'TESTING'} // TODO: replace with a real target
          };

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC).then(function (response) {
            if (response.statusCode === 200) {
              return FormResponseDomain.$get(response.responseId)
            }
          });
        }

        function updateResponse(response) {
          if (!FirebaseConnectionService.online) return Promise.reject({code: 'default.error.offline.message'});

          var request = merge({}, response.$serialize(), {
            method: 'form-responses/update',
            id: response.$id,
            fields: FormUtils.serializeDates(response.fields)
          });

          return FirebaseQueueService.addTask(request, AuthService.getUserId(), ROOT_API_COLLECTION, ROOT_API_SPEC);
        }

        /**
         * @typedef {Object} MergedField
         * @property {string} id - the id of the current field. Both the form and response fields share the same id
         * @property {Object} field - the form field definition
         * @property {Object} response - the response field data
         */

        /**
         * Retrieves the response's corresponding version form fields list and merges it with the response fields list
         * @returns {Promise.<Array.<MergedField>>|*}
         */
        function getFields() {
          __getServices();
          return Promise.props({
            responseVersion: FormDomain.getVersion(this.form, this.formVersion),
            currentVersion: FormDomain.getCurrentVersion(this.form)
          }).then(_cleanVersion.bind(this))
            .then(_mergeFields.bind(this))
            .catch(function (e) {
              console.error("failed to merge fields", e);
            });
        }

        function _cleanVersion(data) {
          if (!data.currentVersion || (data.responseVersion && data.currentVersion.id === data.responseVersion.id)) {
            // the form is no longer allowed for new responses, or the version matches
            return data.responseVersion;

          } else {
            var filter = __createFieldFilter(data.currentVersion.fields, data.responseVersion && data.responseVersion.fields);
            var effectiveVersion = {outOfSync: true, fieldsFilter: filter};
            // shallow object clone
            for (var key in data.currentVersion) {
              if (data.currentVersion.hasOwnProperty(key))
                effectiveVersion[key] = data.currentVersion[key];
            }
            return effectiveVersion;
          }
        }

        // Extract the previous field versions into the filter, if present
        function __createFieldFilter(fields, previousFields) {
          var i, length, id, previousField;
          var filter = {};
          if (previousFields) {
            for (i = 0, length = fields.length; i < length; i++) {
              id = fields[i].id;
              previousField = find(previousFields, createIdFilter(id));
              filter[id] = previousField || true;
            }
          } else {
            for (i = 0, length = fields.length; i < length; i++) {
              filter[fields[i].id] = true;
            }
          }
          return filter;
        }

        function _mergeFields(version) {
          if (!version) {
            return null;
          } else if (version.outOfSync) {
            return __mergeFieldsOutOfSync(version, this);
          } else {
            return __mergeFieldsInSync(version, this);
          }
        }

        function __mergeFieldsInSync(version, response) {
          var i = version.fields.length;
          var mergedFields = new Array(i);
          mergedFields.formVersion = version.id;

          while (i--) {
            mergedFields[i] = {
              id: version.fields[i].id,
              field: version.fields[i],
              response: copy(response.fields[i])
            }
          }

          return mergedFields;
        }

        function __mergeFieldsOutOfSync(version, response) {
          var mergedFields = [];
          var fieldsFilter = version.fieldsFilter;
          mergedFields.formVersion = version.id;
          for (var i = 0, length = version.fields.length; i < length; i++) {
            var formField = version.fields[i];
            var id = formField.id;
            mergedFields[i] = {
              id: id,
              field: formField
            };

            var responseField = find(response.fields, createIdFilter(id));

            if (responseField) {
              if (responseField.legacy || (isObject(fieldsFilter[id]) && fieldsFilter[id].version === formField.version)) {
                mergedFields[i].response = copy(responseField);
              } else {
                mergedFields[i].response = createFromFormField(formField, false, true);
                if (__typeIsCompatible(formField, responseField))
                  mergedFields[i].response.result = __convertCompatibleResult(formField, fieldsFilter[id], responseField);
              }
            } else {
              mergedFields[i].response = createFromFormField(formField, true, true);
            }
          }

          delete version.fieldsFilter; // kill references to any stored fields so they can be garbage collected sooner
          return mergedFields;
        }

        // TODO: remember to handle edge cases list text_short number vs text mode and list select one vs select many
        function __typeIsCompatible(formField, responseField) {
          if (formField.type === responseField.type) {
            return true;
          } else {
            // TODO: implement method stub
          }
        }

        // TODO: remember to handle edge cases list text_short number vs text mode and list select one vs select many
        function __convertCompatibleResult(formField, previousField, responseField) {
          if (formField.type === responseField.type) {
            return __convertForSameType(formField, previousField, responseField);
          } else {
            // TODO: implement method stub
          }
        }

        function __convertForSameType(formField, previousField, responseField) {
          if (!responseField.result)
            return __defaultResultFromType(formField.type, formField.options);

          switch (formField.type) {
            case 'text_short':
              return __convert_text_short(formField, previousField, responseField);

            case 'location':
            case 'date':
            case 'duration':
            case 'text_long':
              return copy(responseField.result);

            case 'scale':
              return __convert_scale(formField, previousField, responseField);

            case 'list':
              return __convert_list(formField, previousField, responseField);

            // List: array of strings
            // TODO: verify these types are implemented correctly
            case 'file':
            case 'reference_link':
              if (isObject(responseField.result) || isArray(responseField.result)) {
                return copy(responseField.result);
              } else {
                return responseField.result;
              }
          }
        }

        function __convert_text_short(formField, previousField, responseField) {
          if (formField.options.isNumber === (previousField && previousField.options.isNumber)) {
            return copy(responseField.result);

          } else if (formField.options.isNumber) {
            if (responseField.result.value === null) {
              return copy(responseField.result);
            } else {
              var converted = parseFloat(responseField.result.value);
              if (isNaN(converted)) {
                return __defaultResultFromType(formField.type, formField.options);
              } else {
                var result = copy(responseField.result);
                result.value = converted;
                return result;
              }
            }

          } else {
            return copy(responseField.result);
          }
        }

        function __convert_scale(formField, previousField, responseField) {
          if (formField.options.range === (previousField && previousField.options.range)) {
            return copy(responseField.result);

          } else {
            var result = copy(responseField.result);
            if (formField.options.range) {
              result.high = formField.options.endNumber;
            } else {
              delete result.high;
            }
            return result;
          }
        }

        // TODO: need to review and implement each path correctly. some are suspect now that each path is well defined with the new comments
        function __convert_list(formField, previousField, responseField) {
          var itemsMap, i, length;
          if (!formField.options.selectMany && formField.options.selectMany === (previousField && previousField.options.selectMany)) {
            // both are select 1
            return __listItemExists(responseField.result, formField.options)
              ? copy(responseField.result)
              : __defaultResultFromType(formField.type, formField.options);

          } else if (formField.options.selectMany && (previousField && previousField.options.selectMany)) {
            // both are select many
            if (!responseField.result) {
              return __defaultResultFromType(formField.type, formField.options);
            } else {
              itemsMap = __convertListItemsToMap(formField.options.items);
              var newResult = [];
              for (i = 0, length = responseField.result.length; i < length; i++) {
                if (itemsMap[responseField.result[i].id]) {
                  newResult.push(copy(itemsMap[responseField.result[i].id]));
                }
              }
              return newResult;
            }
          } else if (!formField.options.selectMany) {
            // new is select 1, old is select many
            if (isArray(responseField.result) && responseField.result.length === 1) {
              return __listItemExists(responseField.result[0], formField.options)
                ? copy(responseField.result[0])
                : __defaultResultFromType(formField.type, formField.options);

            } else if (isArray(responseField.result) && responseField.result.length > 1) {
              itemsMap = __convertListItemsToMap(formField.options.items);
              for (i = 0, length = responseField.result.length; i < length; i++) {
                if (itemsMap[responseField.result[i].id]) {
                  return copy(itemsMap[responseField.result[i].id]);
                }
              }
              return __defaultResultFromType(formField.type, formField.options);

            } else {
              return __defaultResultFromType(formField.type, formField.options);
            }

          } else {
            // new is select many, old is select 1
            return __listItemExists(responseField.result, formField.options)
              ? copy(responseField.result)
              : __defaultResultFromType(formField.type, formField.options);
          }
        }

        function __convertListItemsToMap(items) {
          var result = {};
          for (var i = 0, length = items.length; i < length; i++) {
            result[items[i].id] = items[i];
          }
          return result;
        }

        function createFromFormField(formField, canBePlaceholder, preexisting) {
          if (formField.legacy && canBePlaceholder) {
            return {
              id: formField.id,
              legacy: true
            };
          } else {
            return {
              id: formField.id,
              type: formField.type,
              result: __defaultResultFromType(formField.type, formField.options, preexisting)
            };
          }
        }

        function __defaultResultFromType(fieldType, options, preexisting) {
          switch (fieldType) {
            case 'text_short':
            case 'text_long':
              return {value: null};
            case 'scale':
              if (options.range) {
                return {value: options.startNumber || 0, high: options.endNumber || 0};
              } else {
                return {value: options.startNumber || 0};
              }
            case 'duration':
              return {value: 1, unit: options.defaultUnit};
            case 'date':
              if (preexisting) {
                return {value: null};
              } else {
                var date = new Date();
                date.setSeconds(0, 0);
                return {value: date};
              }
            case 'location':
              return null; //TODO: initialize field. null? empty GeoJSON? user's location?
            // These types each store an array of results
            case 'list': // TODO: make initial selection null when null support is properly added to the field template
              return !options.selectMany ? null : [];
            // List: array of strings
            case 'file':
            case 'reference_link':
              // File and reference: an array of link objects {id: collection:}
              return [];
          }
        }

        function validateFieldResult(field, formField, errors) {
          errors = errors || [];
          if (field.legacy) // skip legacy placeholders
            return errors;

          var options = formField.options;
          var result = __getValue(field, options);

          if (field.isRequired && !__isNotBlank(result)) {
            errors.push(new $domainValidation.FieldError({
              rejectedValue: result,
              code: 'form.response.fields.required.message',
              defaultCode: 'default.blank.message',
              field: field.id,
              arguments: {property: formField.name, rejectedValue: result}
            }));
            return errors;
          }

          // TODO(forms): complete options validation by type
          switch (field.type) {
            case 'text_short':
              if (options.isNumber && !isNumber(result)) {
                errors.push(new $domainValidation.FieldError({
                  rejectedValue: result,
                  code: 'form.response.fields.text_short.isnumber.message',
                  defaultCode: 'default.blank.message',
                  field: field.id,
                  arguments: {property: formField.name, rejectedValue: result}
                }));
              }
              break;
            case 'text_long':
              if (options.minLength && options.maxLength) {
                if (!isString(result) || result.length < options.minLength || result.length > options.maxLength) {
                  errors.push(new $domainValidation.FieldError({
                    rejectedValue: result,
                    code: 'form.response.fields.text_long.range.message',
                    defaultCode: 'default.invalid.range.message',
                    field: field.id,
                    arguments: {property: formField.name, rejectedValue: result}
                  }));
                }
              } else if (options.minLength && (!isString(result) || result.length < options.minLength)) {
                errors.push(new $domainValidation.FieldError({
                  rejectedValue: result,
                  code: 'form.response.fields.text_long.minlength.message',
                  defaultCode: 'default.invalid.min.message',
                  field: field.id,
                  arguments: {property: formField.name, rejectedValue: result}
                }));
              } else if (options.maxLength && (!isString(result) || result.length > options.maxLength)) {
                errors.push(new $domainValidation.FieldError({
                  rejectedValue: result,
                  code: 'form.response.fields.text_long.maxlength.message',
                  defaultCode: 'default.invalid.max.message',
                  field: field.id,
                  arguments: {property: formField.name, rejectedValue: result}
                }));
              }
              break;
            case 'scale':
              break;
            case 'duration':
              break;
            case 'date':
              break;
            case 'location':
              break;
            case 'list':
              //TODO: check for number selected
              break;
            case 'file':
              break;
            case 'reference_link':
              break;
          }

          return errors;
        }

        function __getValue(field, options) {
          var result = field.result;
          switch (field.type) {
            case 'text_short':
            case 'text_long':
            case 'scale':
            case 'date':
              return isObject(result) && getPrimitive(result.value);

            case 'duration':
            case 'location':
              return isObject(result) && result || null;

            // These types each store an array of results
            case 'list':
              return (!options.selectMany ? isObject(result) : isArray(result)) && result || null;

            // List: array of strings
            case 'file':
            case 'reference_link':
              // File and reference: an array of link objects {id: collection:}
              return isArray(result) && result || null;
          }
        }

        function getPrimitive(value) {
          return value ? value : value === 0 || value === '' ? value : null;
        }

        function __isNotBlank(field, options) {
          var result = field.result;
          switch (field.type) {
            case 'text_short':
              if (options.isNumber)
                return isNumber(result);
            /* fall-through */
            case 'text_long':
              return isObject(result) && isString(result.value) && (result.value !== '');

            case 'scale':
            case 'duration':
              return isObject(result) && isNumber(result.value);

            case 'date':
              return isObject(result) && isDate(result.value);

            case 'location':
              return isObject(result);

            // These types each store an array of results
            case 'list':
              if (options.selectMany) {
                return isArray(result) && result.length > 0;
              } else {
                return isObject(result);
              }

            // List: array of strings
            case 'file':
            case 'reference_link':
              // File and reference: an array of link objects {id: collection:}
              return isArray(result) && result.length > 0;
          }
        }

        function __listItemExists(item, options) {
          if (!isObject(item)) {
            return false;
          } else {
            return !!find(options.items, createIdFilter(item.id));
          }
        }

        function getForm() {
          __getServices();
          return FormDomain.$get(this.form);
        }

        // TODO: adjust this
        function toString() {
          var name = this.form && this.form.title;
          var by = this.createdBy && this.createdBy.toString();
          return name + ' submitted by ' + by + ' on ' + $filter('date')(this.lastUpdated);
        }

        // Service Locator Pattern
        function __getServices() {
          if (!FormDomain)
            FormDomain = $injector.get('FormDomain')
        }

        return FormResponseDomain;
      }]
  };

  function createIdFilter(id) {
    return function (it) {
      return it.id === id;
    }
  }
};



// WEBPACK FOOTER //
// ./src/js/forms/domain/FormResponseDomain.js